/**
 * How a therapist's adopted tools are grouped wherever they are listed.
 *
 * Until now each dock kept its own hand-written `DOCK_ORDER`, which meant the
 * order was a fact about four components rather than about the tools. With a
 * fourth tool arriving — and the second one that belongs to systemic work —
 * a flat list stops saying anything: Agenda, Pagos, Constelaciones and
 * Genograma sit side by side with no reason for the order they are in.
 *
 * So the grouping is derived from what the catalogue already knows about each
 * tool. Administration first — the things a therapist does between patients,
 * reached dozens of times a day — then the clinical instruments, gathered by
 * the way of working they belong to. A fifth tool needs a catalogue row, not a
 * dock change.
 */

import { THERAPY_TYPES } from "../../../shared/domain/therapyTypes";
import { ToolCategory, type TherapyTool, type TherapyType } from "./types";

export interface ToolGroup {
    /** Stable key: `administration`, `therapy:<type>`, or `other`. */
    key: string;
    /** i18n key for the group's full name — what assistive tech is told. */
    labelKey: string;
    /**
     * i18n key for a short form, where the surface is a dock rather than a
     * page. "Terapia Sistémica / Familiar" is the honest name of the therapy
     * and four times too long to sit over four icons.
     */
    shortLabelKey?: string;
    tools: TherapyTool[];
}

/**
 * Short names for the groups a dock can show today.
 *
 * Deliberately not derived: a therapy's catalogue name is written to be
 * unambiguous in a filter, and shortening it by rule ("everything before the
 * slash") would produce a different kind of wrong for each entry.
 */
const SHORT_LABELS: Record<string, string> = {
    administration: "marketplace.dockGroups.administration",
    "therapy:systemicFamily": "marketplace.dockGroups.systemicFamily",
    "therapy:emdr": "marketplace.dockGroups.emdr",
};

const ADMINISTRATION_KEY = "administration";
const OTHER_KEY = "other";

/**
 * Within a group, tools keep a fixed order rather than whatever the endpoint
 * returned, so the dock does not reshuffle between therapists — or between two
 * loads for the same one.
 */
const WITHIN_GROUP_ORDER = [
    "scheduler",
    "payments",
    "alchely-meeting",
    "ai-insights",
    "constellation",
    "genogram-ecomap",
    "meditation",
    "emdr",
];

const withinGroup = (a: TherapyTool, b: TherapyTool): number => {
    const ranked = (tool: TherapyTool) => {
        const index = WITHIN_GROUP_ORDER.indexOf(tool.slug);
        return index === -1 ? WITHIN_GROUP_ORDER.length : index;
    };
    const difference = ranked(a) - ranked(b);
    // A third-party tool the list does not know about falls back to its name,
    // which is at least the same order every time.
    return difference !== 0 ? difference : a.slug.localeCompare(b.slug);
};

const isAdministration = (tool: TherapyTool): boolean =>
    (tool.categories ?? []).includes(ToolCategory.ADMINISTRATION);

/** A tool with several therapy types is filed under its first, never twice. */
const primaryTherapyType = (tool: TherapyTool): TherapyType | undefined =>
    tool.therapy_types?.[0];

export const groupToolsForDock = (tools: TherapyTool[]): ToolGroup[] => {
    const administration: TherapyTool[] = [];
    const byTherapy = new Map<TherapyType, TherapyTool[]>();
    const other: TherapyTool[] = [];

    for (const tool of tools) {
        if (isAdministration(tool)) {
            administration.push(tool);
            continue;
        }
        const therapy = primaryTherapyType(tool);
        if (!therapy) {
            other.push(tool);
            continue;
        }
        const bucket = byTherapy.get(therapy) ?? [];
        bucket.push(tool);
        byTherapy.set(therapy, bucket);
    }

    const groups: ToolGroup[] = [];

    if (administration.length > 0) {
        groups.push({
            key: ADMINISTRATION_KEY,
            labelKey: "marketplace.categories.administration",
            shortLabelKey: SHORT_LABELS[ADMINISTRATION_KEY],
            tools: administration.sort(withinGroup),
        });
    }

    // Ordered by the canonical therapy-type list rather than by insertion, so
    // the groups themselves do not move around either.
    for (const therapy of THERAPY_TYPES) {
        const bucket = byTherapy.get(therapy as TherapyType);
        if (!bucket?.length) continue;
        groups.push({
            key: `therapy:${therapy}`,
            labelKey: `session.therapyOptions.${therapy}`,
            shortLabelKey: SHORT_LABELS[`therapy:${therapy}`],
            tools: bucket.sort(withinGroup),
        });
    }

    if (other.length > 0) {
        groups.push({
            key: OTHER_KEY,
            labelKey: "marketplace.categories.tool",
            shortLabelKey: SHORT_LABELS[OTHER_KEY],
            tools: other.sort(withinGroup),
        });
    }

    return groups;
};

/** The grouped tools, flattened — for a surface with no room for headings. */
export const orderToolsForDock = (tools: TherapyTool[]): TherapyTool[] =>
    groupToolsForDock(tools).flatMap((group) => group.tools);
